// en-ZA presentation through Intl (CLAUDE.md: never a hardcoded format). Dates `10 Sept 2026`, times `11:24`, money `R 1 234.56`.
const TZ = "Africa/Johannesburg";
const LOCALE = "en-ZA";

let timezone = TZ;
/** The organisation's timezone, applied once the session view is known. */
export function setTimezone(tz: string): void { timezone = tz; }

const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(LOCALE, { timeZone: timezone, ...opts });

const toDate = (iso: string | Date): Date => (typeof iso === "string" ? new Date(iso) : iso);

export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = toDate(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return fmt({ day: "numeric", month: "short", year: "numeric" }).format(d);
}

export function formatTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = toDate(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return fmt({ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d);
}

export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  return `${formatDate(iso)} ${formatTime(iso)}`;
}

/** `28 Aug` — a day of the current year where the year goes without saying (the stock screens' movement times, FIXTURE §5). */
export function formatDayMonth(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = toDate(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // The day and month exactly as formatDate prints them ("03 Sept 2026" → "03 Sept"), so the two never disagree on one screen.
  return fmt({ day: "numeric", month: "short" }).format(d);
}

/** `Thursday 10 September 2026` for the Today heading. */
export function formatLongDate(d: Date): string {
  return fmt({ weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(d);
}

/** `Thu, 10 Sept 2026` for a date inside a sentence (UC-APPOINTMENTS-006 step 3): never a raw `YYYY-MM-DD`. */
export function formatShortDate(d: Date): string {
  return fmt({ weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(d);
}

/** Branch-local calendar date `YYYY-MM-DD` for query filters. */
export function localDate(d: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** The organisation-zone wall clock of an instant, re-expressed as a UTC timestamp (Intl only — never a hardcoded offset). */
function wallClockMs(utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(utcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") === 24 ? 0 : get("hour"), get("minute"), get("second"));
}

/** Milliseconds the organisation's zone is ahead of UTC at an instant. */
const offsetMs = (utcMs: number): number => wallClockMs(utcMs) - Math.floor(utcMs / 1000) * 1000;

/** The instant (ms) of local midnight at the start of a `YYYY-MM-DD` date in the organisation's zone. */
function localMidnightMs(date: string): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d);
  let result = guess - offsetMs(guess);
  const check = offsetMs(result);
  if (guess - check !== result) result = guess - check;
  return result;
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** `YYYY-MM-DD` plus a number of days, as a calendar date. */
/**
 * Whether `s` is a calendar date `YYYY-MM-DD` that exists. The shape alone lets `2026-13-01` and `2026-02-30` through, and a
 * formatter given one throws — a date typed or kept in the address must pass this before a screen uses it (review S8).
 */
export function isLocalDate(s: string | null | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/**
 * The UTC window `[00:00:00.000, 23:59:59.999]` of a branch-local calendar date, as the ISO instants the governance filters
 * take (`from`/`to` are instants and the range includes both ends). Null for anything but a `YYYY-MM-DD` date.
 */
export function localDayWindow(date: string | null | undefined): { from: string; to: string } | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const start = localMidnightMs(date);
  const end = localMidnightMs(addDays(date, 1)) - 1;
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
}

/**
 * Integer cents to `R 1 234.56` (DESIGN.md: space after R, plain space thousands, point decimal, U+2212 minus). Digits and
 * grouping come from Intl; the two separators are the design system's, which differ from ICU's en-ZA comma decimal.
 */
export function formatMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return "—";
  const abs = Math.abs(cents);
  const parts = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }).formatToParts(abs / 100);
  const text = parts.map((p) => (p.type === "group" ? " " : p.type === "decimal" ? "." : p.value)).join("");
  return `${cents < 0 ? "−" : ""}R ${text}`;
}

/** A whole count with the design system's plain-space thousands, as `formatMoney` groups: `50 000`, `1 842`. */
export function formatCount(n: number): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0, useGrouping: true }).formatToParts(n).map((p) => (p.type === "group" ? " " : p.value)).join("");
}

/**
 * A measurement to a fixed number of decimals — an index `1.600`, a curve `8.5`, a power `−4.00` — with the design system's
 * point decimal and U+2212 minus (DESIGN.md), the digits from Intl as `formatMoney` takes them; no grouping.
 */
export function formatDecimal(n: number | null | undefined, places: number, o: { signed?: boolean } = {}): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  const parts = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: places, maximumFractionDigits: places, useGrouping: false }).formatToParts(Math.abs(n));
  const text = parts.map((p) => (p.type === "decimal" ? "." : p.value)).join("");
  if (/^0(\.0+)?$/.test(text)) return text;
  return `${n < 0 ? "−" : o.signed && n > 0 ? "+" : ""}${text}`;
}

/** A file's size in whole KB; a file under 1 KB reads "< 1 KB", never "0 KB" (UC-PATIENTS-018 step 5). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return "< 1 KB";
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }).format(Math.round(bytes / 1024))} KB`;
}

/**
 * The " · " between the parts of a meta or sub-line, with a no-break space before the dot: when the line wraps, the dot stays at
 * the end of the line it follows and the next line never starts with a separator.
 */
export const DOT = "\u00a0· ";

/**
 * Basis points as a percentage, `12.5%`, `1 000%` — with the design system's point decimal and plain-space thousands, as
 * `formatMoney` writes them (ICU's en-ZA gives `12,5%` and a no-break space), the digits from Intl.
 */
export function percentFromBp(bp: number): string {
  const parts = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2, useGrouping: true }).formatToParts(bp / 100);
  return `${parts.map((p) => (p.type === "group" ? " " : p.type === "decimal" ? "." : p.type === "minusSign" ? "−" : p.value)).join("")}%`;
}

export const initialsOf = (first: string, last: string): string => `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
